import { SparklesIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface PromptInputSectionProps {
  prompt: string;
  isDisabled: boolean;
  isGeneratingPrompt: boolean;
  isStoryboardGuide?: boolean;
  onPromptChange: (prompt: string) => void;
  onExpandClick: () => void;
  onAutoGenerateClick: () => void;
  onStoryboardGuideChange?: (value: boolean) => void;
}

export default function PromptInputSection({
  prompt,
  isDisabled,
  isGeneratingPrompt,
  isStoryboardGuide = false,
  onPromptChange,
  onExpandClick,
  onAutoGenerateClick,
  onStoryboardGuideChange
}: PromptInputSectionProps) {
  const { t } = useTranslation('editor');

  return (
    <div className="px-4 pt-3">
      <div className="relative">
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          disabled={isDisabled}
          className="w-full h-20 bg-gray-800 text-gray-200 text-sm resize-none outline-none placeholder-gray-500 disabled:opacity-50 rounded-lg p-3 pr-10"
          placeholder={t('common.promptPlaceholder')}
        />
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            onClick={onExpandClick}
            disabled={isDisabled}
            className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
            title={t('common.expandEdit')}
          >
            <ArrowsPointingOutIcon className="h-4 w-4" />
          </button>
        </div>
        <button
          onClick={onAutoGenerateClick}
          disabled={isDisabled || isGeneratingPrompt}
          className="absolute bottom-2 right-2 p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
          title={t('common.autoGeneratePrompt')}
        >
          {isGeneratingPrompt ? (
            <div className="h-4 w-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <SparklesIcon className="h-4 w-4" />
          )}
        </button>
      </div>
      {onStoryboardGuideChange && (
        <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isStoryboardGuide}
            onChange={(e) => onStoryboardGuideChange(e.target.checked)}
            disabled={isDisabled}
            className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-900"
          />
          <span className="text-xs text-gray-400">{t('common.storyboardGuideOption')}</span>
        </label>
      )}
    </div>
  );
}
